import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Divider,
  Dropdown,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  Modal,
  ModalDialog,
  Stack,
  Tooltip,
  Typography,
} from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import ArticleIcon from "@mui/icons-material/Article";
import CancelIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteIcon from "@mui/icons-material/Delete";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import LockIcon from "@mui/icons-material/Lock";
import MicIcon from "@mui/icons-material/Mic";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PublicIcon from "@mui/icons-material/Public";
import SearchIcon from "@mui/icons-material/Search";
import SaveIcon from "@mui/icons-material/Save";

import Page from "../../../../components/Pages/Page";
import PageHeader from "../../../../components/PageHeader";
import { useWorkspaceContext } from "../../../../providers/WorkspaceProvider";
import TranscriptFolderNameButton from "./TranscriptFolderNameButton";
import { useTranscripts } from "../../hooks/useTranscripts";
import {
  Transcript,
  TranscriptFolder,
  TranscriptFolderDeletePreview,
  TranscriptVisibility,
} from "../../types/transcripts";
import {
  filterTranscriptsForLocation,
  getBulkSelectedTranscriptIds,
  getBulkSelectionCount,
  getTranscriptFolderPath,
  isTranscriptLocationGenerationCurrent,
  transcriptNavigationReducer,
  TranscriptLocation,
  sortTranscripts,
} from "./transcriptFolderNavigation";

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleString() : "—";

const visibilityColor = (visibility: string) =>
  visibility === "Workspace" ? "primary" : "neutral";

type EditingTarget =
  | { type: "folder"; id: number; value: string }
  | { type: "transcript"; id: number; value: string }
  | null;

type ConfirmationAction =
  | {
      type: "batch-delete";
      folderIds: number[];
      transcriptIds: number[];
      transcriptCount: number;
      folderCount: number;
    }
  | {
      type: "folder-delete";
      folder: TranscriptFolder;
      preview: TranscriptFolderDeletePreview;
    }
  | { type: "publish"; transcriptIds: number[] }
  | null;

type DropTarget = number | "root" | null;

const titleMatches = (transcript: Transcript, query: string) => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;
  return (
    transcript.title.toLowerCase().includes(normalized) ||
    (transcript.transcript_text ?? "").toLowerCase().includes(normalized)
  );
};

const TranscriptList: React.FC = () => {
  const navigate = useNavigate();
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";
  const {
    createFolder,
    deleteFolder,
    deleteTranscript,
    listFolders,
    listTranscripts,
    previewDeleteFolder,
    updateBatchVisibility,
    updateFolder,
    updateTranscript,
  } = useTranscripts();

  const [folders, setFolders] = useState<TranscriptFolder[]>([]);
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<number>>(
    new Set(),
  );
  const [navigation, dispatchNavigation] = useReducer(
    transcriptNavigationReducer,
    {
      location: { type: "all" },
      locationGeneration: 0,
      selectedTranscriptIds: new Set<number>(),
      selectedFolderIds: new Set<number>(),
    },
  );
  const locationGenerationRef = useRef(navigation.locationGeneration);
  const { location, selectedTranscriptIds, selectedFolderIds } = navigation;
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newFolderName, setNewFolderName] = useState("");
  const [editing, setEditing] = useState<EditingTarget>(null);
  const [searchText, setSearchText] = useState("");
  const [transcriptSort, setTranscriptSort] = useState<
    "recent" | "alphabetical"
  >("recent");
  const [confirmationAction, setConfirmationAction] =
    useState<ConfirmationAction>(null);
  const [dragOverFolderId, setDragOverFolderId] = useState<DropTarget>(null);
  const [draggingFolderId, setDraggingFolderId] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);
    try {
      const [folderRows, transcriptRows] = await Promise.all([
        listFolders(workspaceId),
        listTranscripts(workspaceId, null),
      ]);
      setFolders(folderRows);
      setTranscripts(transcriptRows);
      setExpandedFolderIds((current) => {
        if (current.size > 0) return current;
        return new Set(folderRows.map((folder) => folder.folder_id));
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load transcripts",
      );
    } finally {
      setLoading(false);
    }
  }, [listFolders, listTranscripts, workspaceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const searchMatchedTranscripts = useMemo(
    () =>
      transcripts.filter((transcript) => titleMatches(transcript, searchText)),
    [searchText, transcripts],
  );

  const visibleTranscripts = useMemo(() => {
    const rows =
      location.type === "all"
        ? searchMatchedTranscripts.filter(
            (transcript) => transcript.folder_id == null,
          )
        : filterTranscriptsForLocation(searchMatchedTranscripts, location);
    return sortTranscripts(rows, transcriptSort);
  }, [location, searchMatchedTranscripts, transcriptSort]);

  const transcriptsByFolder = useMemo(() => {
    const grouped = new Map<number | "root", Transcript[]>();
    for (const transcript of searchMatchedTranscripts) {
      const key = transcript.folder_id ?? "root";
      grouped.set(key, [...(grouped.get(key) ?? []), transcript]);
    }
    return grouped;
  }, [searchMatchedTranscripts]);

  const foldersByParent = useMemo(() => {
    const grouped = new Map<number | null, TranscriptFolder[]>();
    for (const folder of folders) {
      const key = folder.parent_folder_id ?? null;
      grouped.set(key, [...(grouped.get(key) ?? []), folder]);
    }
    for (const [key, rows] of grouped) {
      grouped.set(
        key,
        rows.sort((left, right) => left.name.localeCompare(right.name)),
      );
    }
    return grouped;
  }, [folders]);

  const folderById = useMemo(
    () => new Map(folders.map((folder) => [folder.folder_id, folder])),
    [folders],
  );

  const folderPathById = useMemo(
    () =>
      new Map(
        folders.map((folder) => [
          folder.folder_id,
          getTranscriptFolderPath(folder.folder_id, folders)
            .map((part) => part.name)
            .join(" / "),
        ]),
      ),
    [folders],
  );

  const selectedFolderPath = useMemo(
    () =>
      location.type === "folder"
        ? getTranscriptFolderPath(location.folderId, folders)
        : [],
    [folders, location],
  );

  const collectDescendantFolderIds = useCallback(
    (folderId: number): Set<number> => {
      const found = new Set<number>();
      const stack = [folderId];
      while (stack.length > 0) {
        const current = stack.pop();
        if (current == null || found.has(current)) continue;
        found.add(current);
        for (const child of foldersByParent.get(current) ?? []) {
          stack.push(child.folder_id);
        }
      }
      return found;
    },
    [foldersByParent],
  );

  const isFolderDescendant = useCallback(
    (candidateFolderId: number, ancestorFolderId: number): boolean =>
      collectDescendantFolderIds(ancestorFolderId).has(candidateFolderId),
    [collectDescendantFolderIds],
  );

  const selectedCascadeRootFolderIds = useMemo(
    () =>
      Array.from(selectedFolderIds).filter((folderId) => {
        let parentId = folderById.get(folderId)?.parent_folder_id ?? null;
        const seen = new Set<number>();
        while (parentId != null && !seen.has(parentId)) {
          if (selectedFolderIds.has(parentId)) return false;
          seen.add(parentId);
          parentId = folderById.get(parentId)?.parent_folder_id ?? null;
        }
        return true;
      }),
    [folderById, selectedFolderIds],
  );

  const selectedTranscriptIdsOutsideSelectedFolders = useCallback(
    (rootFolderIds: number[]): number[] => {
      const selectedFolderSubtreeIds = new Set<number>();
      for (const folderId of rootFolderIds) {
        for (const descendantId of collectDescendantFolderIds(folderId)) {
          selectedFolderSubtreeIds.add(descendantId);
        }
      }
      return Array.from(selectedTranscriptIds).filter((transcriptId) => {
        const transcript = transcripts.find(
          (candidate) => candidate.transcript_id === transcriptId,
        );
        return (
          transcript?.folder_id == null ||
          !selectedFolderSubtreeIds.has(transcript.folder_id)
        );
      });
    },
    [collectDescendantFolderIds, selectedTranscriptIds, transcripts],
  );

  const hasRepositoryRows = folders.length > 0 || visibleTranscripts.length > 0;
  const selectedCount = getBulkSelectionCount(navigation);
  const hasSelectedTranscripts = selectedTranscriptIds.size > 0;

  const startTranscript = () => {
    navigate(`/workspace/${workspaceId}/meridian/transcripts/new`);
  };

  const openTranscript = (transcriptId: number) => {
    navigate(`/workspace/${workspaceId}/meridian/transcripts/${transcriptId}`);
  };

  const toggleFolderExpanded = (folderId: number) => {
    setExpandedFolderIds((current) => {
      const next = new Set(current);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  const toggleFolderSelection = (folderId: number, checked: boolean) => {
    dispatchNavigation({
      type: "folder_selection_changed",
      folderId,
      selected: checked,
    });
  };

  const toggleTranscriptSelection = (
    transcriptId: number,
    checked: boolean,
  ) => {
    dispatchNavigation({
      type: "transcript_selection_changed",
      transcriptId,
      selected: checked,
    });
  };

  const changeLocation = (nextLocation: TranscriptLocation) => {
    locationGenerationRef.current += 1;
    setConfirmationAction(null);
    dispatchNavigation({ type: "location_changed", location: nextLocation });
  };

  const clearSelection = () => {
    dispatchNavigation({ type: "clear_selection" });
  };

  const moveTranscriptToFolder = async (
    transcript: Transcript,
    folderId: number | null,
  ) => {
    if (!workspaceId || transcript.folder_id === folderId) return;
    setBusy(true);
    setError(null);
    try {
      await updateTranscript(workspaceId, transcript.transcript_id, {
        folder_id: folderId,
      });
      if (folderId != null) {
        setExpandedFolderIds((current) => new Set([...current, folderId]));
      }
      await load();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to move transcript",
      );
    } finally {
      setBusy(false);
      setDragOverFolderId(null);
      setDraggingFolderId(null);
    }
  };

  const moveFolderToParent = async (
    folder: TranscriptFolder,
    parentFolderId: number | null,
  ) => {
    if (!workspaceId || folder.parent_folder_id === parentFolderId) return;
    if (parentFolderId === folder.folder_id) {
      setError("A folder cannot be moved into itself.");
      return;
    }
    if (
      parentFolderId != null &&
      isFolderDescendant(parentFolderId, folder.folder_id)
    ) {
      setError("A folder cannot be moved into one of its subfolders.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await updateFolder(workspaceId, folder.folder_id, {
        parent_folder_id: parentFolderId,
      });
      if (parentFolderId != null) {
        setExpandedFolderIds(
          (current) => new Set([...current, parentFolderId]),
        );
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to move folder");
    } finally {
      setBusy(false);
      setDragOverFolderId(null);
      setDraggingFolderId(null);
    }
  };

  const handleTranscriptDragStart = (
    event: React.DragEvent,
    transcript: Transcript,
  ) => {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData(
      "application/x-meridian-transcript-id",
      String(transcript.transcript_id),
    );
    event.dataTransfer.setData(
      "application/json",
      JSON.stringify({
        type: "transcript",
        transcriptId: transcript.transcript_id,
      }),
    );
  };

  const handleFolderDragStart = (
    event: React.DragEvent,
    folder: TranscriptFolder,
  ) => {
    setDraggingFolderId(folder.folder_id);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData(
      "application/x-meridian-folder-id",
      String(folder.folder_id),
    );
    event.dataTransfer.setData(
      "application/json",
      JSON.stringify({ type: "folder", folderId: folder.folder_id }),
    );
  };

  const canDropOnFolder = (event: React.DragEvent, folderId: number) => {
    if (
      event.dataTransfer.types.includes("application/x-meridian-transcript-id")
    )
      return true;
    if (!event.dataTransfer.types.includes("application/x-meridian-folder-id"))
      return false;
    const draggedFolderId =
      draggingFolderId ??
      Number(event.dataTransfer.getData("application/x-meridian-folder-id"));
    if (!draggedFolderId || draggedFolderId === folderId) return false;
    return !isFolderDescendant(folderId, draggedFolderId);
  };

  const canDropOnRoot = (event: React.DragEvent) => {
    if (
      event.dataTransfer.types.includes("application/x-meridian-transcript-id")
    )
      return true;
    if (!event.dataTransfer.types.includes("application/x-meridian-folder-id"))
      return false;
    const draggedFolderId =
      draggingFolderId ??
      Number(event.dataTransfer.getData("application/x-meridian-folder-id"));
    const draggedFolder = draggedFolderId
      ? folderById.get(draggedFolderId)
      : null;
    return Boolean(draggedFolder?.parent_folder_id);
  };

  const handleDragEnd = () => {
    setDragOverFolderId(null);
    setDraggingFolderId(null);
  };

  const handleFolderDragOver = (event: React.DragEvent, folderId: number) => {
    if (canDropOnFolder(event, folderId)) {
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = "move";
      setDragOverFolderId(folderId);
    }
  };

  const handleRepositoryDragOver = (event: React.DragEvent) => {
    if (canDropOnRoot(event)) {
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      setDragOverFolderId("root");
    }
  };

  const handleDropToFolder = (
    event: React.DragEvent,
    folderId: number | null,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOverFolderId(null);
    setDraggingFolderId(null);
    const rawTranscriptId = event.dataTransfer.getData(
      "application/x-meridian-transcript-id",
    );
    if (rawTranscriptId) {
      const transcriptId = Number(rawTranscriptId);
      const transcript = transcripts.find(
        (candidate) => candidate.transcript_id === transcriptId,
      );
      if (!transcript) return;
      void moveTranscriptToFolder(transcript, folderId);
      return;
    }

    const rawFolderId = event.dataTransfer.getData(
      "application/x-meridian-folder-id",
    );
    if (!rawFolderId) return;
    const draggedFolderId = Number(rawFolderId);
    const folder = folderById.get(draggedFolderId);
    if (!folder) return;
    void moveFolderToParent(folder, folderId);
  };

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!workspaceId || !name) return;
    setBusy(true);
    setError(null);
    try {
      const folder = await createFolder(workspaceId, name);
      setNewFolderName("");
      setExpandedFolderIds(
        (current) => new Set([...current, folder.folder_id]),
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create folder");
    } finally {
      setBusy(false);
    }
  };

  const saveInlineRename = async () => {
    if (!workspaceId || !editing) return;
    const value = editing.value.trim();
    if (!value) return;
    setBusy(true);
    setError(null);
    try {
      if (editing.type === "folder") {
        await updateFolder(workspaceId, editing.id, { name: value });
      } else {
        await updateTranscript(workspaceId, editing.id, { title: value });
      }
      setEditing(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to rename item");
    } finally {
      setBusy(false);
    }
  };

  const executeBatchDelete = async (
    folderIds: number[],
    transcriptIds: number[],
  ) => {
    if (!workspaceId || (folderIds.length === 0 && transcriptIds.length === 0))
      return;
    setBusy(true);
    setError(null);
    try {
      await Promise.all([
        ...transcriptIds.map((id) => deleteTranscript(workspaceId, id)),
        ...folderIds.map((id) => deleteFolder(workspaceId, id)),
      ]);
      clearSelection();
      setConfirmationAction(null);
      await load();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to delete selected items",
      );
    } finally {
      setBusy(false);
    }
  };

  const executeDeleteFolder = async (folder: TranscriptFolder) => {
    if (!workspaceId) return;
    setBusy(true);
    setError(null);
    try {
      await deleteFolder(workspaceId, folder.folder_id);
      dispatchNavigation({
        type: "folder_removed",
        folderId: folder.folder_id,
      });
      setConfirmationAction(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete folder");
    } finally {
      setBusy(false);
    }
  };

  const executeVisibility = async (
    transcriptIds: number[],
    visibility: TranscriptVisibility,
  ) => {
    if (!workspaceId || transcriptIds.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await updateBatchVisibility(
        workspaceId,
        transcriptIds,
        visibility,
        visibility === "Workspace",
      );
      clearSelection();
      setConfirmationAction(null);
      await load();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to update selected transcripts",
      );
    } finally {
      setBusy(false);
    }
  };

  const executeBatchVisibility = async (visibility: TranscriptVisibility) => {
    if (selectedTranscriptIds.size === 0) return;
    await executeVisibility(
      getBulkSelectedTranscriptIds(navigation),
      visibility,
    );
  };

  const updateTranscriptVisibility = async (
    transcript: Transcript,
    visibility: TranscriptVisibility,
  ) => {
    if (!workspaceId || transcript.visibility === visibility) return;
    setBusy(true);
    setError(null);
    try {
      await updateBatchVisibility(
        workspaceId,
        [transcript.transcript_id],
        visibility,
        visibility === "Workspace",
      );
      await load();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to update transcript visibility",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleSingleTranscriptVisibility = (
    transcript: Transcript,
    visibility: TranscriptVisibility,
  ) => {
    if (visibility === "Workspace") {
      setConfirmationAction({
        type: "publish",
        transcriptIds: [transcript.transcript_id],
      });
    } else {
      void updateTranscriptVisibility(transcript, visibility);
    }
  };

  const handleBatchDelete = async () => {
    if (!workspaceId || selectedCount === 0) return;
    const startedGeneration = locationGenerationRef.current;
    setBusy(true);
    setError(null);
    try {
      const folderIds = selectedCascadeRootFolderIds;
      const previews = await Promise.all(
        folderIds.map((folderId) => previewDeleteFolder(workspaceId, folderId)),
      );
      if (
        !isTranscriptLocationGenerationCurrent(
          startedGeneration,
          locationGenerationRef.current,
        )
      ) {
        return;
      }
      const transcriptIds =
        selectedTranscriptIdsOutsideSelectedFolders(folderIds);
      const transcriptCount =
        transcriptIds.length +
        previews.reduce((sum, preview) => sum + preview.transcript_count, 0);
      const folderCount = previews.reduce(
        (sum, preview) => sum + preview.total_folder_count,
        0,
      );
      setConfirmationAction({
        type: "batch-delete",
        folderIds,
        transcriptIds,
        transcriptCount,
        folderCount,
      });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to prepare delete confirmation",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteFolder = async (folder: TranscriptFolder) => {
    if (!workspaceId) return;
    const startedGeneration = locationGenerationRef.current;
    setBusy(true);
    setError(null);
    try {
      const preview = await previewDeleteFolder(workspaceId, folder.folder_id);
      if (
        !isTranscriptLocationGenerationCurrent(
          startedGeneration,
          locationGenerationRef.current,
        )
      ) {
        return;
      }
      setConfirmationAction({ type: "folder-delete", folder, preview });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to prepare folder delete confirmation",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleBatchVisibility = (visibility: TranscriptVisibility) => {
    if (visibility === "Workspace") {
      if (selectedTranscriptIds.size === 0) return;
      setConfirmationAction({
        type: "publish",
        transcriptIds: getBulkSelectedTranscriptIds(navigation),
      });
      return;
    }
    void executeBatchVisibility("Private");
  };

  const confirmationContent = (() => {
    if (!confirmationAction) return null;
    if (confirmationAction.type === "publish") {
      return {
        title: "Make transcripts public in this workspace?",
        body: (
          <>
            <Typography level="body-sm">
              This will make {confirmationAction.transcriptIds.length}{" "}
              transcript
              {confirmationAction.transcriptIds.length === 1 ? "" : "s"} visible
              to every user who has access to this workspace.
            </Typography>
            <Typography level="body-sm" color="neutral">
              The transcripts may contain notes, names, and sensitive
              information. This does not make them public on the internet.
            </Typography>
          </>
        ),
        confirmLabel: "Make Public in Workspace",
        confirmColor: "primary" as const,
        onConfirm: () =>
          executeVisibility(confirmationAction.transcriptIds, "Workspace"),
      };
    }
    if (confirmationAction.type === "folder-delete") {
      const transcriptCount = confirmationAction.preview.transcript_count;
      const subfolderCount = confirmationAction.preview.subfolder_count;
      return {
        title: "Delete transcript folder?",
        body: (
          <>
            <Typography level="body-sm">
              Delete folder “{confirmationAction.folder.name}”?
            </Typography>
            <Typography level="body-sm" color="neutral">
              This will permanently delete {transcriptCount} transcript
              {transcriptCount === 1 ? "" : "s"} and {subfolderCount} nested
              folder
              {subfolderCount === 1 ? "" : "s"} in this folder.
            </Typography>
            <Alert color="danger" variant="soft">
              This cannot be undone. Transcripts inside deleted folders are
              deleted with the folder.
            </Alert>
          </>
        ),
        confirmLabel: "Delete folder",
        confirmColor: "danger" as const,
        onConfirm: () => executeDeleteFolder(confirmationAction.folder),
      };
    }
    return {
      title: "Delete selected transcript items?",
      body: (
        <>
          <Typography level="body-sm">
            Delete {confirmationAction.transcriptCount} transcript
            {confirmationAction.transcriptCount === 1 ? "" : "s"} and{" "}
            {confirmationAction.folderCount} folder
            {confirmationAction.folderCount === 1 ? "" : "s"}?
          </Typography>
          <Typography level="body-sm" color="neutral">
            Folders are deleted recursively. Transcripts inside selected folders
            are deleted with those folders.
          </Typography>
          <Alert color="danger" variant="soft">
            This cannot be undone.
          </Alert>
        </>
      ),
      confirmLabel: "Delete selected",
      confirmColor: "danger" as const,
      onConfirm: () =>
        executeBatchDelete(
          confirmationAction.folderIds,
          confirmationAction.transcriptIds,
        ),
    };
  })();

  const renderEditableTitle = (
    target: Exclude<EditingTarget, null>,
    label: string,
    titleLevel: "body-sm" | "title-sm" = "body-sm",
  ) => {
    const isEditing = editing?.type === target.type && editing.id === target.id;
    if (isEditing) {
      return (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ flex: 1, minWidth: 0 }}
        >
          <Input
            size="sm"
            autoFocus
            aria-label={`Rename ${label}`}
            value={editing.value}
            onChange={(event) =>
              setEditing({ ...editing, value: event.target.value })
            }
            onKeyDown={(event) => {
              if (event.key === "Enter") void saveInlineRename();
              if (event.key === "Escape") setEditing(null);
            }}
            sx={{ flex: "1 1 180px", minWidth: 0 }}
          />
          <Tooltip title="Save name">
            <span>
              <IconButton
                size="sm"
                aria-label={`Save ${label}`}
                onClick={saveInlineRename}
                disabled={!editing.value.trim() || busy}
              >
                <SaveIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Cancel rename">
            <IconButton
              size="sm"
              aria-label={`Cancel renaming ${label}`}
              variant="plain"
              color="neutral"
              onClick={() => setEditing(null)}
            >
              <CancelIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      );
    }
    return (
      <Button
        variant="plain"
        color="neutral"
        size="sm"
        onClick={() => setEditing({ ...target, value: label })}
        endDecorator={<DriveFileRenameOutlineIcon fontSize="small" />}
        sx={{
          justifyContent: "flex-start",
          px: 0.5,
          minHeight: 28,
          minWidth: 0,
          flex: 1,
          "& .MuiButton-endDecorator": { opacity: 0.5 },
        }}
      >
        <Typography level={titleLevel} noWrap sx={{ minWidth: 0 }}>
          {label}
        </Typography>
      </Button>
    );
  };

  const renderTranscriptRow = (transcript: Transcript, depth = 1) => {
    const selected = selectedTranscriptIds.has(transcript.transcript_id);
    return (
      <Box
        key={`transcript-${transcript.transcript_id}`}
        draggable={!busy}
        onDragStart={(event) => handleTranscriptDragStart(event, transcript)}
        onDragEnd={handleDragEnd}
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          py: 1,
          px: 1,
          pl: `${depth * 24 + 8}px`,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: selected ? "primary.softBg" : "background.body",
          "&:hover": {
            bgcolor: selected ? "primary.softBg" : "background.level1",
          },
        }}
      >
        <Checkbox
          size="sm"
          checked={selected}
          onChange={(event) =>
            toggleTranscriptSelection(
              transcript.transcript_id,
              event.target.checked,
            )
          }
          sx={{ mr: 1 }}
        />
        <ArticleIcon sx={{ color: "primary.500", fontSize: 20, mr: 1 }} />
        <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.25}>
          {renderEditableTitle(
            {
              type: "transcript",
              id: transcript.transcript_id,
              value: transcript.title,
            },
            transcript.title,
            "title-sm",
          )}
          <Typography level="body-xs" color="neutral" noWrap>
            {formatDate(transcript.modified_date)} · {transcript.language} ·{" "}
            {transcript.status}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 1 }}>
          <Dropdown>
            <MenuButton
              slots={{ root: Button }}
              slotProps={{
                root: {
                  size: "sm",
                  variant: "soft",
                  color: visibilityColor(transcript.visibility) as any,
                  "aria-label": `Visibility ${transcript.title}`,
                } as any,
              }}
            >
              {transcript.visibility === "Workspace"
                ? "Workspace public"
                : "Private"}
            </MenuButton>
            <Menu placement="bottom-end">
              <MenuItem
                disabled={transcript.visibility === "Private" || busy}
                onClick={() =>
                  void updateTranscriptVisibility(transcript, "Private")
                }
              >
                Make private
              </MenuItem>
              <MenuItem
                disabled={transcript.visibility === "Workspace" || busy}
                onClick={() =>
                  handleSingleTranscriptVisibility(transcript, "Workspace")
                }
              >
                Make public in workspace
              </MenuItem>
            </Menu>
          </Dropdown>
          <Dropdown>
            <MenuButton
              slots={{ root: IconButton }}
              slotProps={{
                root: {
                  size: "sm",
                  variant: "plain",
                  "aria-label": `Move ${transcript.title}`,
                },
              }}
            >
              <MoreVertIcon />
            </MenuButton>
            <Menu placement="bottom-end">
              <MenuItem disabled>
                <DriveFileMoveIcon fontSize="small" />
                Move to folder…
              </MenuItem>
              <MenuItem
                disabled={transcript.folder_id == null || busy}
                onClick={() => void moveTranscriptToFolder(transcript, null)}
              >
                No folder
              </MenuItem>
              {folders.map((folder) => (
                <MenuItem
                  key={folder.folder_id}
                  disabled={transcript.folder_id === folder.folder_id || busy}
                  onClick={() =>
                    void moveTranscriptToFolder(transcript, folder.folder_id)
                  }
                >
                  {folderPathById.get(folder.folder_id) ?? folder.name}
                </MenuItem>
              ))}
            </Menu>
          </Dropdown>
          <Tooltip title="Open transcript">
            <IconButton
              size="sm"
              variant="plain"
              onClick={() => openTranscript(transcript.transcript_id)}
              aria-label={`Open ${transcript.title}`}
            >
              <OpenInNewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete transcript">
            <IconButton
              size="sm"
              variant="plain"
              color="danger"
              aria-label={`Delete ${transcript.title}`}
              onClick={() =>
                setConfirmationAction({
                  type: "batch-delete",
                  folderIds: [],
                  transcriptIds: [transcript.transcript_id],
                  transcriptCount: 1,
                  folderCount: 0,
                })
              }
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    );
  };

  const renderFolderRow = (
    folder: TranscriptFolder,
    depth = 0,
    ancestors = new Set<number>(),
  ): React.ReactNode => {
    if (ancestors.has(folder.folder_id)) return null;
    const nextAncestors = new Set(ancestors);
    nextAncestors.add(folder.folder_id);
    const expanded = expandedFolderIds.has(folder.folder_id);
    const selected = selectedFolderIds.has(folder.folder_id);
    const childTranscripts = transcriptsByFolder.get(folder.folder_id) ?? [];
    const childFolders = foldersByParent.get(folder.folder_id) ?? [];
    const isDragOver = dragOverFolderId === folder.folder_id;
    const isCurrentFolder =
      location.type === "folder" && location.folderId === folder.folder_id;
    return (
      <React.Fragment key={`folder-${folder.folder_id}`}>
        <Box
          draggable={!busy}
          onDragStart={(event) => handleFolderDragStart(event, folder)}
          onDragEnd={handleDragEnd}
          onDragOver={(event) => handleFolderDragOver(event, folder.folder_id)}
          onDragLeave={(event) => {
            const relatedTarget = event.relatedTarget as HTMLElement | null;
            if (
              !relatedTarget ||
              !event.currentTarget.contains(relatedTarget)
            ) {
              setDragOverFolderId(null);
            }
          }}
          onDrop={(event) => handleDropToFolder(event, folder.folder_id)}
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            py: 1,
            px: 1,
            pl: `${depth * 24 + 8}px`,
            borderBottom: "1px solid",
            borderColor: isDragOver ? "primary.500" : "divider",
            bgcolor:
              isDragOver || isCurrentFolder || selected
                ? "primary.softBg"
                : "background.level1",
            outline: isDragOver ? "2px solid" : "none",
            outlineColor: "primary.500",
            transition: "background-color 0.15s, outline 0.15s",
            "&:hover": {
              bgcolor:
                isDragOver || selected ? "primary.softBg" : "background.level2",
            },
          }}
        >
          <Checkbox
            size="sm"
            checked={selected}
            onChange={(event) =>
              toggleFolderSelection(folder.folder_id, event.target.checked)
            }
            sx={{ mr: 1 }}
          />
          <IconButton
            size="sm"
            variant="plain"
            onClick={() => toggleFolderExpanded(folder.folder_id)}
            sx={{ mr: 0.5 }}
            aria-label={
              expanded ? `Collapse ${folder.name}` : `Expand ${folder.name}`
            }
          >
            {expanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
          </IconButton>
          {expanded ? (
            <FolderOpenIcon
              sx={{ color: "warning.500", fontSize: 20, mr: 1 }}
            />
          ) : (
            <FolderIcon sx={{ color: "warning.500", fontSize: 20, mr: 1 }} />
          )}
          {editing?.type === "folder" && editing.id === folder.folder_id ? (
            renderEditableTitle(
              { type: "folder", id: folder.folder_id, value: folder.name },
              folder.name,
              "title-sm",
            )
          ) : (
            <TranscriptFolderNameButton
              name={folder.name}
              current={isCurrentFolder}
              onClick={() =>
                changeLocation({ type: "folder", folderId: folder.folder_id })
              }
            />
          )}
          <Tooltip title={`Rename ${folder.name}`}>
            <IconButton
              size="sm"
              variant="plain"
              onClick={() =>
                setEditing({
                  type: "folder",
                  id: folder.folder_id,
                  value: folder.name,
                })
              }
              aria-label={`Rename ${folder.name}`}
            >
              <DriveFileRenameOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography
            level="body-xs"
            color="neutral"
            sx={{ mx: 1, whiteSpace: "nowrap" }}
          >
            {childTranscripts.length} transcript
            {childTranscripts.length === 1 ? "" : "s"}
            {childFolders.length > 0
              ? ` · ${childFolders.length} folder${childFolders.length === 1 ? "" : "s"}`
              : ""}
          </Typography>
          <Dropdown>
            <MenuButton
              slots={{ root: IconButton }}
              slotProps={{
                root: {
                  size: "sm",
                  variant: "plain",
                  "aria-label": `Move ${folder.name}`,
                },
              }}
            >
              <MoreVertIcon />
            </MenuButton>
            <Menu placement="bottom-end">
              <MenuItem disabled>
                <DriveFileMoveIcon fontSize="small" />
                Move folder…
              </MenuItem>
              <MenuItem
                disabled={folder.parent_folder_id == null || busy}
                onClick={() => void moveFolderToParent(folder, null)}
              >
                Top level
              </MenuItem>
              {folders
                .filter(
                  (candidate) =>
                    candidate.folder_id !== folder.folder_id &&
                    !isFolderDescendant(candidate.folder_id, folder.folder_id),
                )
                .map((candidate) => (
                  <MenuItem
                    key={candidate.folder_id}
                    disabled={
                      folder.parent_folder_id === candidate.folder_id || busy
                    }
                    onClick={() =>
                      void moveFolderToParent(folder, candidate.folder_id)
                    }
                  >
                    {folderPathById.get(candidate.folder_id) ?? candidate.name}
                  </MenuItem>
                ))}
            </Menu>
          </Dropdown>
          <Tooltip title="Delete folder">
            <IconButton
              size="sm"
              variant="plain"
              color="danger"
              onClick={() => void handleDeleteFolder(folder)}
              aria-label={`Delete ${folder.name}`}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
        {expanded && (
          <>
            {sortTranscripts(childTranscripts, transcriptSort).map(
              (transcript) => renderTranscriptRow(transcript, depth + 1),
            )}
            {childFolders.map((childFolder) =>
              renderFolderRow(childFolder, depth + 1, nextAncestors),
            )}
          </>
        )}
      </React.Fragment>
    );
  };

  return (
    <Page>
      <PageHeader>
        <Box>
          <Typography level="h1">Transcripts</Typography>
          <Typography level="body-sm" color="neutral">
            Capture live text transcripts and organize them in this workspace.
          </Typography>
        </Box>
        <Button startDecorator={<MicIcon />} onClick={startTranscript}>
          Start recording
        </Button>
      </PageHeader>

      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={2}>
        <Card variant="outlined">
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", lg: "flex-end" }}
          >
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Search transcripts</FormLabel>
              <Input
                startDecorator={<SearchIcon />}
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder="Search by title or text"
              />
            </FormControl>
            <FormControl sx={{ minWidth: { xs: "100%", lg: 260 } }}>
              <FormLabel>New folder</FormLabel>
              <Input
                value={newFolderName}
                onChange={(event) => setNewFolderName(event.target.value)}
                placeholder="Folder name"
              />
            </FormControl>
            <Button
              startDecorator={<AddIcon />}
              onClick={handleCreateFolder}
              disabled={!newFolderName.trim() || busy}
            >
              Create folder
            </Button>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <Typography level="body-sm" color="neutral" sx={{ flex: 1 }}>
              {loading
                ? "Loading transcripts…"
                : selectedCount
                  ? `${selectedCount} selected`
                  : `${transcripts.length} transcript${transcripts.length === 1 ? "" : "s"} in current workspace`}
            </Typography>
            <Button
              size="sm"
              variant="soft"
              color="primary"
              startDecorator={<PublicIcon />}
              disabled={!hasSelectedTranscripts || busy}
              onClick={() => handleBatchVisibility("Workspace")}
            >
              Make Public in Workspace
            </Button>
            <Button
              size="sm"
              variant="soft"
              color="neutral"
              startDecorator={<LockIcon />}
              disabled={!hasSelectedTranscripts || busy}
              onClick={() => handleBatchVisibility("Private")}
            >
              Make Private
            </Button>
            <Button
              size="sm"
              variant="soft"
              color="danger"
              startDecorator={<DeleteIcon />}
              disabled={selectedCount === 0 || busy}
              onClick={() => void handleBatchDelete()}
            >
              Delete
            </Button>
            {selectedCount > 0 && (
              <Button
                size="sm"
                variant="plain"
                color="neutral"
                onClick={clearSelection}
              >
                Clear
              </Button>
            )}
          </Stack>
        </Card>

        {loading ? (
          <Card
            variant="outlined"
            sx={{ display: "flex", justifyContent: "center", py: 6 }}
          >
            <CircularProgress />
          </Card>
        ) : !hasRepositoryRows ? (
          <Card variant="outlined" sx={{ p: 4, textAlign: "center" }}>
            <Typography level="title-md">No transcripts yet</Typography>
            <Typography level="body-sm" color="neutral" sx={{ mt: 1 }}>
              Start a recording to capture a text transcript from your
              microphone.
            </Typography>
            <Button
              startDecorator={<MicIcon />}
              sx={{ mt: 2 }}
              onClick={startTranscript}
            >
              Start recording
            </Button>
          </Card>
        ) : (
          <Card
            variant="outlined"
            sx={{
              p: 0,
              overflow: "hidden",
              minWidth: 0,
              outline: dragOverFolderId === "root" ? "2px solid" : "none",
              outlineColor: "primary.500",
              bgcolor:
                dragOverFolderId === "root" ? "primary.softBg" : "transparent",
              transition: "background-color 0.15s, outline 0.15s",
            }}
            onDragOver={handleRepositoryDragOver}
            onDragLeave={(event) => {
              const relatedTarget = event.relatedTarget as HTMLElement | null;
              if (
                !relatedTarget ||
                !event.currentTarget.contains(relatedTarget)
              ) {
                setDragOverFolderId(null);
              }
            }}
            onDrop={(event) => handleDropToFolder(event, null)}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography level="title-md">
                    {location.type === "folder"
                      ? (selectedFolderPath[selectedFolderPath.length - 1]
                          ?.name ?? "Folders and unfiled transcripts")
                      : "Folders and unfiled transcripts"}
                  </Typography>
                  {location.type === "folder" && (
                    <Stack
                      component="nav"
                      aria-label="Transcript breadcrumbs"
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      useFlexGap
                      flexWrap="wrap"
                    >
                      <Button
                        size="sm"
                        variant="plain"
                        color="neutral"
                        onClick={() => changeLocation({ type: "all" })}
                      >
                        All transcripts
                      </Button>
                      {selectedFolderPath.map((folder, index) => (
                        <React.Fragment key={folder.folder_id}>
                          <ChevronRightIcon fontSize="small" />
                          <Button
                            size="sm"
                            variant="plain"
                            color="neutral"
                            aria-current={
                              index === selectedFolderPath.length - 1
                                ? "page"
                                : undefined
                            }
                            onClick={() =>
                              changeLocation({
                                type: "folder",
                                folderId: folder.folder_id,
                              })
                            }
                          >
                            {folder.name}
                          </Button>
                        </React.Fragment>
                      ))}
                    </Stack>
                  )}
                </Box>
                <Button
                  size="sm"
                  variant="outlined"
                  onClick={() =>
                    setTranscriptSort((current) =>
                      current === "recent" ? "alphabetical" : "recent",
                    )
                  }
                  aria-label={`Sort transcripts ${transcriptSort === "recent" ? "alphabetically" : "by date"}`}
                >
                  Sort: {transcriptSort === "recent" ? "Newest first" : "A–Z"}
                </Button>
              </Stack>
            </Box>

            <Stack spacing={0}>
              {(
                foldersByParent.get(
                  location.type === "folder" ? location.folderId : null,
                ) ?? []
              ).map((folder) => renderFolderRow(folder, 0))}

              {location.type === "all" && (
                <>
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      bgcolor: "background.level1",
                    }}
                  >
                    <Typography level="title-sm">
                      Unfiled transcripts
                    </Typography>
                  </Box>
                  {visibleTranscripts.map((transcript) =>
                    renderTranscriptRow(transcript, 0),
                  )}
                  {visibleTranscripts.length === 0 && (
                    <Typography
                      level="body-sm"
                      color="neutral"
                      sx={{ px: 2, py: 2 }}
                    >
                      {folders.length
                        ? "No unfiled transcripts"
                        : "No folders or unfiled transcripts"}
                    </Typography>
                  )}
                </>
              )}

              {location.type === "folder" && (
                <>
                  {visibleTranscripts.map((transcript) =>
                    renderTranscriptRow(transcript, 0),
                  )}
                  {visibleTranscripts.length === 0 &&
                    (foldersByParent.get(location.folderId) ?? []).length ===
                      0 && (
                      <Typography
                        level="body-sm"
                        color="neutral"
                        sx={{ px: 2, py: 2 }}
                      >
                        No transcripts or subfolders in this folder
                      </Typography>
                    )}
                </>
              )}
            </Stack>
          </Card>
        )}
      </Stack>

      <Modal
        open={Boolean(confirmationAction)}
        onClose={() => {
          if (!busy) setConfirmationAction(null);
        }}
      >
        <ModalDialog
          aria-labelledby="transcript-confirmation-title"
          sx={{
            width: "min(520px, calc(100vw - 32px))",
            borderRadius: "lg",
            p: 0,
            overflow: "hidden",
          }}
        >
          {confirmationContent && (
            <Stack>
              <Box
                sx={{
                  px: 3,
                  py: 2.5,
                  bgcolor:
                    confirmationContent.confirmColor === "danger"
                      ? "danger.softBg"
                      : "primary.softBg",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor:
                        confirmationContent.confirmColor === "danger"
                          ? "danger.solidBg"
                          : "primary.solidBg",
                      color: "#fff",
                    }}
                  >
                    {confirmationContent.confirmColor === "danger" ? (
                      <DeleteIcon />
                    ) : (
                      <PublicIcon />
                    )}
                  </Box>
                  <Box>
                    <Typography
                      id="transcript-confirmation-title"
                      level="title-lg"
                    >
                      {confirmationContent.title}
                    </Typography>
                    <Typography level="body-xs" color="neutral">
                      Current workspace only
                    </Typography>
                  </Box>
                </Stack>
              </Box>
              <Stack spacing={1.5} sx={{ px: 3, py: 2.5 }}>
                {confirmationContent.body}
                {confirmationAction?.type === "publish" && (
                  <Alert color="warning" variant="soft">
                    Workspace-public transcripts are visible to all users with
                    access to this workspace.
                  </Alert>
                )}
              </Stack>
              <Stack
                direction="row"
                spacing={1}
                justifyContent="flex-end"
                sx={{
                  px: 3,
                  py: 2,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Button
                  variant="plain"
                  color="neutral"
                  disabled={busy}
                  onClick={() => setConfirmationAction(null)}
                >
                  Cancel
                </Button>
                <Button
                  color={confirmationContent.confirmColor}
                  loading={busy}
                  onClick={() => void confirmationContent.onConfirm()}
                >
                  {confirmationContent.confirmLabel}
                </Button>
              </Stack>
            </Stack>
          )}
        </ModalDialog>
      </Modal>
    </Page>
  );
};

export default TranscriptList;
